import { html, css } from 'lit';
import WarpElement from '@warp-ds/elements-core';
import { button } from '@warp-ds/css/component-classes';
import pulse from '@finn-no/pulse-sdk';
import tracking from '@finn-no/analytics';
import { i18n } from '@lingui/core';

const converter = (value) => {
    if (!value) return null;

    try {
        const v = JSON.parse(value);
        if (Array.isArray(v)) {
            return v.map((val) => val.value || val);
        }
        if (v.value) {
            return [v.value];
        }
    } catch (err) {
        // noop
    }

    if (typeof value === 'string') {
        return [value];
    }

    return null;
};
class MFPRealestateRethink extends WarpElement {
    static properties = {
        market: { attribute: true, type: String },
        baseurl: { attribute: true, type: String },
        backgroundImageUrl: { attribute: true, type: String },
        backgroundPosition: { attribute: true, type: String },
        contractImageUrl: { attribute: true, type: String },
        headerText: { attribute: true, type: String },
        placesToLive: { attribute: true, type: String },
        css: { attribute: true, converter },
    };

    static styles = [
        WarpElement.styles,
        css`
            *,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0;box-sizing:border-box;border-style:solid;border-width:0;border-color:var(--w-s-color-border,var(--w-s-color-border-default))}html{font-size:62.5%}body{background-color:var(--w-s-color-background,var(--w-s-color-background-default));min-height:100%;margin:0;overflow-y:scroll}body,:host{-webkit-text-size-adjust:100%;tab-size:4;-webkit-tap-highlight-color:transparent;font-family:var(--w-font-family);font-size:var(--w-font-size-m);line-height:var(--w-line-height-m);color:var(--w-s-color-text,var(--w-s-color-text-default))}hr{color:inherit;border-top-width:1px;height:0}abbr:where([title]){-webkit-text-decoration:underline dotted;text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:700}a{cursor:pointer;color:var(--w-s-color-text-link);text-decoration:none}a:hover,a:focus,a:active{-webkit-text-decoration:var(--w-decoration-text-link);-webkit-text-decoration:var(--w-decoration-text-link);text-decoration:var(--w-decoration-text-link)}b,strong{font-weight:700}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace;font-size:1em}sub,sup{vertical-align:baseline;font-size:75%;line-height:0;position:relative}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;font-weight:inherit;line-height:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}button,[type=button],[type=reset],[type=submit]{-webkit-appearance:button}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button{height:auto}::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{margin:0;padding:0;list-style:none}textarea{resize:vertical}input::placeholder,textarea::placeholder{opacity:1;color:var(--w-s-color-text-placeholder)}button,[role=button]{cursor:pointer}:disabled{cursor:default}img,svg,video,canvas,audio,iframe,embed,object{vertical-align:middle;display:block}img,video{max-width:100%;height:auto}h1{font-size:var(--w-font-size-xxl);line-height:var(--w-line-height-xxl)}h2{font-size:var(--w-font-size-xl);line-height:var(--w-line-height-xl)}h3{font-size:var(--w-font-size-l);line-height:var(--w-line-height-l)}h4{font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}h5{font-size:var(--w-font-size-s);line-height:var(--w-line-height-s)}dt,dd{margin:0 16px}h1,h2,h3,h4,h5,ul,ol,dl,p,blockquote{margin:0 0 8px}[hidden]{display:none!important}[tabindex="-1"]:focus:not(:focus-visible){outline:none}legend{float:left;width:100%;margin:0;padding:0;display:table}legend+*{clear:both}fieldset{border:0;min-width:0;margin:0;padding:.01em 0 0}body:not(:-moz-handler-blocked) fieldset{display:table-cell}.page-container{background-color:var(--w-s-color-background);max-width:1010px;margin:0;padding:0 16px}@media (min-width:1300px){.page-container{margin-left:auto;margin-right:auto;padding-left:31px;padding-right:31px}}.text-center{text-align:center}.rounded-8{border-radius:8px}.block{display:block}.flex{display:flex}.inline-flex{display:inline-flex}.hidden{display:none}.underline{text-decoration-line:underline}.flex-col{flex-direction:column}.gap-16{gap:1.6rem}.gap-8{gap:.8rem}.col-span-1{grid-column:span 1/span 1}.col-span-2{grid-column:span 2/span 2}.grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}.overflow-hidden{overflow:hidden}.items-center{align-items:center}.-right-40{right:-4rem}.top-48{top:4.8rem}.absolute{position:absolute}.relative{position:relative}.static{position:static}.max-w-none{max-width:none}.w-1\\/8{width:12.5%}.-mx-16{margin-left:-1.6rem;margin-right:-1.6rem}.mx-16{margin-left:1.6rem;margin-right:1.6rem}.my-16{margin-top:1.6rem;margin-bottom:1.6rem}.my-auto{margin-top:auto;margin-bottom:auto}.mb-0{margin-bottom:0}.mb-16{margin-bottom:1.6rem}.mb-24{margin-bottom:2.4rem}.mb-32{margin-bottom:3.2rem}.mb-4{margin-bottom:.4rem}.ml-auto{margin-left:auto}.mr-20{margin-right:2rem}.pr-2{padding-right:.2rem}.font-bold{font-weight:700}.sr-only{clip:rect(0,0,0,0);white-space:nowrap;border-width:0;width:1px;height:1px;margin:-1px;padding:0;position:absolute;overflow:hidden}.whitespace-nowrap{white-space:nowrap}.text-m{font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}.text-ml{font-size:var(--w-font-size-ml);line-height:var(--w-line-height-ml)}@media (min-width:480px){.sm\\:inline{display:inline}.sm\\:grid{display:grid}.sm\\:m-0{margin:0}}@media (min-width:768px){.md\\:block{display:block}.md\\:grid{display:grid}.md\\:absolute{position:absolute}.md\\:max-w-max{max-width:max-content}.md\\:my-24{margin-top:2.4rem;margin-bottom:2.4rem}.md\\:mb-64{margin-bottom:6.4rem}.md\\:mb-80{margin-bottom:8rem}.md\\:ml-auto{margin-left:auto}.md\\:mr-96{margin-right:9.6rem}}@media (min-width:990px){.lg\\:rounded-0{border-radius:0}.lg\\:rounded-8{border-radius:8px}.lg\\:mr-80{margin-right:8rem}};

            :host {
                display: block;
            }
            .tips {
                max-width: 100%;
                background-color: var(--w-bluegray-50);
                padding: 24px;
            }
            .tips ol {
            }
            @media (min-width: 768px) {
                .tips {
                    max-width: 478px;
                    padding: 42px;
                }
            }
            @media (min-width: 990px) {
                .tips {
                    background-color: transparent;
                    padding: 0px;
                }
                .tips ol {
                    background-color: var(--w-bluegray-50);
                    padding: 42px;
                }
            }

            .no-gutters {
                background-color: var(--f-white);
                margin: 0;
                max-width: 1010px;
                padding: 0;
                padding-left: 0px;
                padding-right: 0px;
            }

            @media (min-width: 768px) {
                .no-gutters {
                    margin-top: -60px;
                }
            }

            @media (min-width: 1300px) {
                .no-gutters {
                    margin-left: auto;
                    margin-right: auto;
                    padding-left: 16px;
                    padding-right: 16px;
                }
            }

            .text-wrap {
                width: calc(100vw - 172px);
            }
            @media (min-width: 480px) {
                .text-wrap {
                    width: 100%;
                }
            }
            .contract {
                width: 183px;
                height: 200px;
                position: absolute;
                top: -15px;
                right: -45px;
            }
            @media (min-width: 480px) {
                .contract {
                    width: 230px;
                    height: 250px;
                    position: initial;
                }
            }
        `,
    ];

    connectedCallback() {
        super.connectedCallback();

        const messages = JSON.parse(this.getAttribute('messages') || '{}');
        const locale = this.getAttribute('locale') || 'nb';
        i18n.load(locale, messages);
        i18n.activate(locale);

        pulse.trackPageView({
            object: {
                type: 'MarketFrontpage',
            },
            vertical: {
                name: 'realestate',
            },
        });

        tracking.trackPage({});
    }

    firstUpdated() {
        // this is a workaround for the fact that the global search is rendered after the page is loaded
        const slot = this.shadowRoot.querySelector('slot[name="globalSearch"]');
        const assignedNodes = slot.assignedNodes({ flatten: true });

        const divElement = Array.from(assignedNodes).find(
            (node) =>
                node.nodeType === Node.ELEMENT_NODE && node.tagName === 'DIV',
        );

        if (divElement) {
            divElement.classList.remove('hidden');
        }
    }

    render() {
        return html`
            <slot name="topbar"></slot>
            <main style="min-height: 60vw">
                <h1 class="sr-only">Eiendom</h1>
                <realestate-search
                    displayText="${this.headerText}"
                    backgroundImageUrl="${this.backgroundImageUrl}"
                    backgroundPosition="${this.backgroundPosition}"
                    placesToLive="${this.placesToLive}"
                >
                    <div slot="globalSearch">
                        <slot name="globalSearch"></slot>
                    </div>
                </realestate-search>
                <realestate-tabs>
                    <div slot="tabs-selected-for-sale">
                        <slot name="personal-viewings"></slot>
                    </div>
                    <div class="page-container" slot="tabs-selected-for-rent">

                        <div class="relative mb-32 md:mb-64">
                            <realestate-lettings-popular
                                items="${this.getAttribute(
            'popularLettings',
        )}"
                            ></realestate-lettings-popular>
                        </div>

                        <section aria-labelledby="5-tips" class="mb-32 md:mb-80">
                            <h2 id="5-tips" class="mb-24">
                                5 tips fra oss til deg som skal leie
                            </h2>
                            <div class="md:grid grid-cols-3 relative">
                                <img
                                    class="col-span-2 hidden md:block rounded-8"
                                    src="${this.contractImageUrl}"
                                    alt="To unge mennesker ser på mobilen mens de tømmer oppvaskmaskinen"
                                />
                                <div
                                    class="flex flex-col gap-16 md:absolute -right-40 top-48 rounded-8 lg:rounded-0 tips"
                                >
                                    <ol class="lg:rounded-8">
                                        <li class="flex items-center mb-24">
                                            <p
                                                class="text-ml font-bold w-1/8 text-center my-auto ml-3 mr-20 pr-2"
                                            >
                                                1
                                            </p>
                                            <p class="text-m mb-0">
                                                Skriv leiekontrakt med
                                                oppsigelsestid. Bruk FINNs
                                                <a
                                                    class="underline"
                                                    href="https://www.finn.no/eiendom/leiekontrakt.html"
                                                    >leiekontrakt</a
                                                >
                                                helt gratis.
                                            </p>
                                        </li>
                                        <li class="flex items-center mb-24">
                                            <p
                                                class="text-ml font-bold w-1/8 text-center my-auto mr-20"
                                            >
                                                2
                                            </p>
                                            <p class="text-m mb-0">
                                                Sørg for å ha depositum på
                                                egen konto i ditt navn
                                                (depositumskonto)
                                            </p>
                                        </li>
                                        <li class="flex items-center mb-24">
                                            <p
                                                class="text-ml font-bold w-1/8 text-center  my-auto mr-20"
                                            >
                                                3
                                            </p>
                                            <p class="text-m mb-0">
                                                Dokumenter eventuelle feil
                                                eller mangler ved boligen
                                                ved innflytting
                                            </p>
                                        </li>
                                        <li class="flex items-center mb-24">
                                            <p
                                                class="text-ml font-bold w-1/8 text-center my-auto mr-20"
                                            >
                                                4
                                            </p>
                                            <p class="text-m mb-0">
                                                Meld fra til huseier om
                                                eventuelle reparasjoner. Det
                                                er ikke ditt ansvar!
                                            </p>
                                        </li>
                                        <li class="flex items-center mb-4">
                                            <p
                                                class="text-ml font-bold w-1/8 text-center my-auto mr-20"
                                            >
                                                5
                                            </p>
                                            <p class="text-m mb-0">
                                                Husk at strømregning ofte
                                                kommer utenom husleie, ta
                                                dette med i beregningen
                                            </p>
                                        </li>
                                    </ol>
                                    <a
                                        class="${button.secondary} max-w-none md:max-w-max md:mr-96 lg:mr-80 md:ml-auto inline-flex"
                                        href="https://www.finn.no/realestate/artikler/leie-ut-bolig/5-tips-til-deg-som-skal-leie-bolig"
                                        >Jada! Kjør på med flere leietips</a
                                    >
                                </div>
                            </div>
                        </section>
                        <section aria-labelledby="renting-out" class="mb-32 sm:grid grid-cols-3 relative -mx-16 overflow-hidden">
                            <div class="col-span-2 mx-16">
                                <h2 id="renting-out">Leie ut bolig?</h2>
                                <p class="sm:inline mb-16 sm:m-0 text-wrap">
                                    Å leie ut er en fin måte å tjene litt
                                    ekstra penger på. Det er imidlertid et
                                    <a
                                        href="https://www.finn.no/realestate/artikler/leie-ut-bolig/5-ting-du-ma-huske-pa-nar-du-skal-leie-ut-bolig"
                                        >par ting du bør huske på</a
                                    >
                                    dersom du vurderer utleie.
                                </p>
                                <p class="sm:inline sm:m-0">
                                    Spesielt viktig er leiekontrakten! For å
                                    gjøre prosessen enklest mulig, har FINN
                                    utarbeidet en
                                    <a
                                        href="https://www.finn.no/eiendom/leiekontrakt.html"
                                        >leiekontrakt</a
                                    >
                                    i samarbeid med Forbrukerrådet, som
                                    sikrer rettighetene både til utleier og
                                    den som leier.
                                </p>
                                <div class="flex gap-8 my-16 md:my-24">
                                    <a
                                        class="${button.primary} max-w-none md:max-w-max flex gap-8 items-center"
                                        href="https://www.finn.no/innfinn/"
                                        ><svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            fill="none"
                                            viewBox="0 0 16 16"
                                        >
                                            <title>Pluss</title>
                                            <path
                                                stroke="currentColor"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="1.5"
                                                d="M2.5 8h11M8 2.5v11"
                                            ></path></svg
                                        ><span class="whitespace-nowrap"
                                            >Ny utleieannonse</span
                                        ></a
                                    >
                                    <a
                                        class="${button.secondary} max-w-none md:max-w-max"
                                        href="https://www.finn.no/eiendom/leiekontrakt.html"
                                        >Leiekontrakt</a
                                    >
                                </div>
                            </div>
                            <svg
                                class="col-span-1 ml-auto contract"
                                width="230"
                                height="250"
                                viewBox="0 0 230 250"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clip-path="url(#clip0_255_9502)">
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M92.8695 26.8847L221.505 70.9681L146.865 241.067L13.1284 182.971L92.8695 26.8847Z"
                                        fill="#D9E0E8"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M91.8231 13.8726L219.05 62.6613L138.794 229.774L6.92418 166.802L91.8231 13.8726Z"
                                        fill="#E8EEF4"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M41.8235 148.29L44.4985 149.688C54.0644 154.656 61.1823 158.015 73.1281 163.346L80.5023 166.626L82.8354 167.671C94.6614 172.993 103.075 177.161 114.011 183.304C115.142 183.94 115.562 185.373 114.95 186.507C114.337 187.64 112.924 188.044 111.794 187.409L109.733 186.258C99.9082 180.806 91.9404 176.891 80.9846 171.961L71.2876 167.64C58.1761 161.789 50.8135 158.284 39.7231 152.458C38.5754 151.855 38.1152 150.433 38.6952 149.282C39.25 148.181 40.5556 147.727 41.6725 148.217L41.8235 148.29Z"
                                        fill="#C3CCD9"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M75.299 132.967L75.4396 132.946C75.7276 132.914 75.8943 133 76.7646 133.731L77.5621 134.408L77.7576 135.508L77.6515 136.086L77.5508 136.349L77.452 136.543L77.3623 136.688L77.3215 136.747L76.7689 137.297C76.6988 137.36 76.6826 137.364 76.652 137.37L76.4875 137.463L76.2158 137.602C75.948 137.736 75.5594 137.924 75.1267 138.128L74.293 138.517L73.0481 139.086C70.1218 140.417 67.3245 141.488 64.0131 142.433L63.627 142.54L63.4685 142.597C61.1681 143.416 59.8821 144.181 59.1044 145.247L58.9967 145.402C58.3473 146.376 58.2751 146.821 58.683 147.522C59.5537 149.019 62.6546 149.605 67.7011 149.047C69.0785 148.894 70.5595 148.665 72.2225 148.356L72.8549 148.236C73.3185 148.147 73.7849 148.054 74.2841 147.952L78.7016 147.018L79.7188 146.81C81.2951 146.498 82.3015 146.351 83.1169 146.335C84.9301 146.3 85.8867 147.903 85.6198 149.375C85.5366 149.834 85.364 150.231 85.098 150.708C84.8221 151.202 84.4145 151.819 83.8608 152.59L83.0507 153.695C82.6996 154.167 82.3595 154.615 81.8693 155.246L79.3105 158.503L79.4937 158.456C81.652 157.913 83.7502 158.038 85.7862 158.861L86.0635 158.978C88.1576 159.893 90.2081 160.122 92.4416 159.868L92.8626 159.815C93.6867 159.701 96.1405 159.189 95.9649 159.221C96.6268 159.101 97.1096 159.06 97.6353 159.14C98.8111 159.317 99.8025 160.152 100.012 161.472C100.292 163.233 101.523 165.97 102.961 167.308L103.15 167.474C103.782 168.002 104.133 168.086 109.105 168.972C112.536 169.583 115.088 170.261 117.821 171.455C119.011 171.975 119.575 173.37 119.08 174.571C118.585 175.772 117.218 176.324 116.028 175.803C113.849 174.851 111.762 174.272 108.972 173.75L105.529 173.128C101.809 172.438 101.565 172.241 100.218 171.118C98.2834 169.506 96.777 166.847 95.9621 164.396L95.8633 164.089L95.5167 164.163C94.7985 164.313 94.1986 164.422 93.5604 164.51C90.5311 164.93 87.6511 164.702 84.7117 163.512L84.2704 163.326C80.9259 161.865 76.5276 164.032 71.0276 170.554C70.3208 171.392 69.1276 171.61 68.1584 171.079C66.941 170.411 66.6535 169.25 66.8579 168.158C66.9416 167.711 67.1096 167.281 67.3563 166.794C67.6727 166.17 68.1488 165.408 68.7924 164.477L69.0132 164.16C70.0482 162.687 71.3403 160.987 73.3743 158.395L78.0946 152.397L78.4603 151.924L76.9312 152.251L75.2648 152.599C74.8798 152.678 74.5127 152.752 74.1505 152.822L73.7894 152.893C71.7555 153.284 69.9599 153.571 68.2742 153.758C61.4931 154.508 56.8485 153.63 54.6711 149.887C53.5992 148.044 53.4995 146.326 54.1158 144.635L54.1942 144.431L53.9161 144.465C52.7362 144.597 51.5518 144.691 50.3637 144.746L49.472 144.779C48.1839 144.817 47.0945 143.787 47.0388 142.479C46.9831 141.171 47.9822 140.08 49.2703 140.043C53.0856 139.932 56.8791 139.367 60.6203 138.425C60.9519 138.341 61.2775 138.257 61.5976 138.171L62.2017 138.005L63.1805 137.681L65.833 136.824L66.433 136.621C66.625 136.554 66.8097 136.489 66.9898 136.424C68.0737 136.032 69.2969 135.535 70.5477 134.989L71.6266 134.509L72.5691 134.067L74.3194 133.225L74.4989 133.144C74.5452 133.124 74.5838 133.109 74.632 133.093L74.802 133.039C74.8711 133.021 74.9699 133.009 75.299 132.967Z"
                                        fill="#0063FB"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M97.3387 82.7199L97.987 83.8831C98.7109 85.1539 99.1239 85.6641 99.5936 85.8976L99.6771 85.936C100.693 86.3669 101.842 86.5631 104.549 86.8119L105.727 86.9166C108.844 87.1943 110.557 87.4815 112.251 88.1873C114 88.9161 115.172 89.768 117.368 91.7637L119.034 93.2928C120.681 94.7833 121.638 95.4967 122.742 96.0048L123.089 96.1561C123.556 96.3435 123.983 96.4066 126.259 96.532L128.992 96.6736C131.432 96.7967 133.198 96.9745 134.933 97.3328C135.944 97.5417 136.886 97.8059 137.766 98.1373C140.18 99.0459 141.258 100.168 142.172 102.156L142.338 102.531C142.777 103.534 142.991 103.793 143.964 104.252C144.887 104.688 145.615 104.971 146.833 105.377L147.615 105.634C150.932 106.713 152.748 107.504 155.758 109.433C156.854 110.136 157.194 111.599 156.517 112.701C155.841 113.803 154.403 114.126 153.307 113.423C150.841 111.842 149.394 111.183 146.758 110.306L145.394 109.857C144.029 109.401 143.16 109.064 142.046 108.537C139.992 107.568 139.035 106.484 138.201 104.673L138.028 104.284C137.565 103.224 137.376 103.014 136.199 102.571C135.56 102.331 134.851 102.132 134.066 101.97C132.718 101.691 131.274 101.536 129.295 101.425L126.09 101.271C125.583 101.244 125.16 101.216 124.757 101.181C123.2 101.05 122.068 100.837 121.116 100.422C119.286 99.6229 117.992 98.6621 115.757 96.6228L114.359 95.3378C112.538 93.6807 111.646 93.0253 110.532 92.5609C109.479 92.1221 108.165 91.8894 105.756 91.6645L103.767 91.4852C100.827 91.2057 99.4775 90.9543 97.9304 90.298C96.1921 89.5605 95.2828 88.5236 94.0229 86.3244L93.4639 85.3221C92.6438 83.8415 92.2042 83.3136 91.7382 83.1529C90.1074 82.5907 89.159 82.4626 87.6274 82.5166L86.5034 82.5694C84.8398 82.6416 83.5342 82.5604 81.8104 82.1873C80.5467 81.9137 79.7259 80.6561 79.9772 79.3782C80.2284 78.1003 81.4566 77.2862 82.7203 77.5597C83.8722 77.8091 84.729 77.8821 85.8162 77.8557L86.6034 77.8237L86.9985 77.8039C89.2802 77.693 90.8153 77.8587 93.1817 78.6746C95.0424 79.3161 96.0559 80.4606 97.3387 82.7199ZM111.862 61.2326L111.944 62.2548C111.988 62.7105 112.023 62.7766 112.121 62.8234L112.147 62.8349C113.209 63.2853 114.516 63.4996 117.727 63.8142L119.229 63.9621C122.248 64.2741 123.871 64.5746 125.462 65.2377L125.868 65.4121C127.462 66.1199 128.764 66.976 131.406 68.9092L132.849 69.963L133.523 70.4427C134.823 71.3517 135.834 71.9664 136.804 72.4146L137.353 72.6537C137.959 72.9029 138.603 73.1048 140.654 73.66L146.191 75.1402C146.437 75.2071 146.675 75.2723 146.91 75.3373L147.925 75.6224C149.895 76.1838 151.505 76.6964 152.898 77.2206C155.032 78.0237 156.379 79.0373 158.578 81.2311L159.417 82.0806C161.12 83.8167 162.02 84.5711 163.188 85.1223C166.194 86.542 169.652 87.0932 175.94 87.4379C177.228 87.5084 178.23 88.6235 178.179 89.9284C178.129 91.2333 177.044 92.234 175.756 92.1634L174.575 92.0943C168.431 91.71 164.775 91.0626 161.269 89.4074C159.541 88.5913 158.344 87.6218 156.454 85.7196L155.914 85.17C153.801 83.0134 152.81 82.2111 151.331 81.6545L150.681 81.4172C149.45 80.9805 147.997 80.5331 146.232 80.0379L145.042 79.7092L138.807 78.0405C137.313 77.6312 136.634 77.4215 136.055 77.1994L135.827 77.1094C135.715 77.0636 135.602 77.0161 135.484 76.9653L135.254 76.8658C133.776 76.2203 132.386 75.3738 130.577 74.0892L127.911 72.1505C126.01 70.7805 125.01 70.1572 123.896 69.6761L123.743 69.6113C122.652 69.1567 121.227 68.9095 118.409 68.6281L116.543 68.4444C113.361 68.1174 111.95 67.8541 110.4 67.1968C108.161 66.2469 107.494 64.7649 107.267 62.2317L107.206 61.4742C107.18 61.1819 107.155 60.996 107.128 60.8729L107.118 60.8258L106.842 60.7361C105.714 60.3822 104.389 60.1541 102.738 60.0088L102.279 59.9712C101.513 59.9128 100.779 59.8741 99.6102 59.8262L97.7728 59.7495C94.9116 59.6204 93.6895 59.4973 92.0866 59.1503C90.8229 58.8768 90.0021 57.6191 90.2533 56.3412C90.5046 55.0633 91.7328 54.2492 92.9965 54.5227L93.4535 54.6158C94.4634 54.8074 95.4812 54.9035 97.4005 54.9975L100.645 55.1374C101.381 55.1718 101.957 55.2061 102.562 55.2522C104.946 55.434 106.856 55.7601 108.588 56.3574C110.948 57.1709 111.631 58.6395 111.862 61.2326Z"
                                        fill="#C3CCD9"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M61.5929 113.679C59.7714 114.85 50.0923 108.597 37.8383 91.0346C24.5279 71.9746 10.3795 39.263 7.57666 32.4821C5.00582 26.3055 12.3646 21.3696 16.8081 27.0085C21.227 32.5914 42.1872 63.9934 51.7963 82.4705C60.1191 98.3858 63.0968 112.725 61.5929 113.679Z"
                                        fill="#0063FB"
                                    />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M4.47224 43.5526C5.36521 45.9289 6.92364 48.6731 8.57026 51.3903L9.23224 52.4749C11.4454 56.0789 13.6785 59.5414 14.5632 61.9264C16.3926 66.963 14.756 74.5576 14.756 74.5576C14.756 74.5576 20.5682 69.1251 18.4217 61.3348C16.6473 54.9426 10.3087 48.9445 9.7239 43.9126C9.52617 42.23 10.2889 41.537 11.2338 40.8617C16.0354 51.6874 24.4212 69.5625 33.0587 83.6676C33.1196 83.7722 33.2739 84.0065 33.2739 84.0065L47.9039 75.4807C47.9039 75.4807 47.7566 75.2443 47.6872 75.132C45.7023 71.5979 43.4999 67.8495 41.2073 64.0612L40.4805 62.8638C40.1157 62.2647 39.749 61.665 39.3809 61.0654L38.643 59.8668C29.5221 45.0908 19.7431 30.6881 16.8201 26.9793C12.4756 21.4586 5.33844 26.0841 7.46641 32.0775C6.39033 32.4536 5.41078 33.0664 4.75458 33.7716C2.62741 36.082 2.75939 39.0131 4.47224 43.5526Z"
                                        fill="#0000B5"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_255_9502">
                                        <rect
                                            width="230"
                                            height="250"
                                            fill="white"
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                        </section>

                    </div>
                </realestate-tabs>
                <div class="no-gutters">
                    <slot name="recommendations"></slot>
                </div>
            </main>
            <slot name="footer"></slot>
        `;
    }
}

customElements.define('mfp-realestate-rethink', MFPRealestateRethink);
